// frontend/src/components/InstructionNotificationModal.js
// 指導症例通知を開いて、育成対象の経験録（スキルレベル + チェックリスト進捗）を入力するモーダル。
//
// 各 procedure について：
//   - スキルレベル（記入済みチェックが必須）
//   - チェックリスト（存在する場合のみ。記入済みチェックが必須）
// すべての記入済みチェックが ON にならないと保存できない。

import { useState, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { X, ExternalLink, AlertCircle, CheckCircle2, BookOpen, ListChecks } from 'lucide-react';
import { supabase, saveSkillRecords } from '../supabase';
import { useOrganization } from '../contexts/OrganizationContext';
import { writeClientAudit } from '../utils/auditClient';
import SavingOverlay from './ui/SavingOverlay';

const SKILL_LEVELS = [
  '未経験',
  '見学済み',
  '経験1回',
  '経験2回',
  '経験3回以上',
  '外から見守り',
  '独り立ち',
  '指導可',
];

export default function InstructionNotificationModal({ notification, traineeName, onClose }) {
  const navigate = useNavigate();
  const { currentUser } = useOrganization();

  const [existingByProcedureId, setExistingByProcedureId] = useState({});
  const [levels, setLevels] = useState({});                       // proc_id -> level
  const [skillMarked, setSkillMarked] = useState({});             // proc_id -> bool（記入済み）
  const [checklistsByProcId, setChecklistsByProcId] = useState({});
  const [progressByProcId, setProgressByProcId] = useState({});   // proc_id -> [{item_id, ...}]
  const [checklistMarked, setChecklistMarked] = useState({});     // proc_id -> bool（記入済み）

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const procedures = Array.isArray(notification?.procedures) ? notification.procedures : [];
  const trainerName = useMemo(() => (
    currentUser?.user_metadata?.full_name || currentUser?.email || '不明'
  ), [currentUser]);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError('');
      try {
        if (procedures.length === 0) {
          setLoading(false);
          return;
        }
        const procIds = procedures.map((p) => p.id);

        const [{ data: skillData, error: skillErr },
               { data: masterData, error: masterErr },
               { data: progData, error: progErr }] = await Promise.all([
          supabase
            .from('skill_records')
            .select('procedure_id, role, level, label')
            .eq('user_id', notification.trainee_staff_id)
            .eq('organization_id', notification.organization_id)
            .eq('role', notification.role)
            .in('procedure_id', procIds),
          supabase
            .from('procedure_checklists')
            .select('procedure_id, role, checklist_items')
            .eq('organization_id', notification.organization_id)
            .eq('role', notification.role)
            .in('procedure_id', procIds),
          supabase
            .from('checklist_progress')
            .select('procedure_id, role, completed_items')
            .eq('user_id', notification.trainee_staff_id)
            .eq('organization_id', notification.organization_id)
            .eq('role', notification.role)
            .in('procedure_id', procIds),
        ]);
        if (skillErr) throw skillErr;
        if (masterErr) throw masterErr;
        if (progErr) throw progErr;

        const skillMap = {};
        const initLevels = {};
        (skillData || []).forEach((r) => {
          if (!skillMap[r.procedure_id] || (r.label || '') === '') {
            skillMap[r.procedure_id] = r;
          }
        });
        procedures.forEach((p) => { initLevels[p.id] = skillMap[p.id]?.level || ''; });
        setExistingByProcedureId(skillMap);
        setLevels(initLevels);

        const masterMap = {};
        (masterData || []).forEach((row) => {
          masterMap[row.procedure_id] = Array.isArray(row.checklist_items) ? row.checklist_items : [];
        });
        setChecklistsByProcId(masterMap);

        const progMap = {};
        (progData || []).forEach((row) => {
          progMap[row.procedure_id] = Array.isArray(row.completed_items) ? row.completed_items : [];
        });
        procedures.forEach((p) => { if (!progMap[p.id]) progMap[p.id] = []; });
        setProgressByProcId(progMap);

        // 記入済みフラグはすべて未チェックで初期化
        const initMarks = {};
        procedures.forEach((p) => { initMarks[p.id] = false; });
        setSkillMarked(initMarks);
        setChecklistMarked({ ...initMarks });
      } catch (e) {
        console.error('既存データ取得エラー:', e);
        setError('既存の経験録の取得に失敗しました');
      } finally {
        setLoading(false);
      }
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notification?.id]);

  const toggleChecklistItem = (procId, itemId) => {
    setProgressByProcId((prev) => {
      const current = prev[procId] || [];
      const isCompleted = current.some((p) => p.item_id === itemId);
      const next = isCompleted
        ? current.filter((p) => p.item_id !== itemId)
        : [...current, {
            item_id: itemId,
            completed_at: new Date().toISOString().split('T')[0],
            completed_by: trainerName,
          }];
      return { ...prev, [procId]: next };
    });
  };

  // バリデーション：各 procedure で
  //   - 「スキル記入済み」が必須（チェック ON + レベル選択済み）
  //   - チェックリストが存在する場合は「チェックリスト記入済み」も必須
  const procHasChecklist = (procId) => (checklistsByProcId[procId] || []).length > 0;
  const procReady = (procId) => {
    if (!skillMarked[procId]) return false;
    if (!levels[procId]) return false;
    if (procHasChecklist(procId) && !checklistMarked[procId]) return false;
    return true;
  };

  const unreadyCount = procedures.filter((p) => !procReady(p.id)).length;
  const canSave = unreadyCount === 0 && procedures.length > 0;

  const handleSave = async () => {
    setError('');
    if (!canSave) {
      setError('未記入の項目があります。記入済みチェックを ON にしてください。');
      return;
    }
    setSaving(true);
    try {
      // 1. スキル記録
      const records = procedures.map((p) => ({
        user_id: notification.trainee_staff_id,
        procedure_id: p.id,
        role: notification.role,
        level: levels[p.id],
        label: existingByProcedureId[p.id]?.label || '',
      }));
      await saveSkillRecords(records, notification.organization_id);

      // 1.5. 配置履歴 (staff_assignments) に level_after_assignment を反映
      //      surgery_id + trainee_staff_id + role でマッチする行を更新
      try {
        for (const p of procedures) {
          await supabase
            .from('staff_assignments')
            .update({
              level_after_assignment: levels[p.id] || null,
              evaluated_at: new Date().toISOString(),
              evaluated_by: trainerName,
              procedure_id: p.id,
            })
            .eq('organization_id', notification.organization_id)
            .eq('surgery_id', notification.surgery_id)
            .eq('staff_id', notification.trainee_staff_id)
            .eq('role', notification.role);
        }
      } catch (e) {
        console.warn('staff_assignments の level_after_assignment 更新に失敗（継続）:', e?.message);
      }

      // 2. チェックリスト進捗
      for (const proc of procedures) {
        if (!procHasChecklist(proc.id)) continue;
        const items = progressByProcId[proc.id] || [];

        const { data: existing, error: fetchErr } = await supabase
          .from('checklist_progress')
          .select('id')
          .eq('user_id', notification.trainee_staff_id)
          .eq('procedure_id', proc.id)
          .eq('organization_id', notification.organization_id)
          .eq('role', notification.role)
          .maybeSingle();
        if (fetchErr) throw fetchErr;

        if (existing) {
          const { error: updErr } = await supabase
            .from('checklist_progress')
            .update({ completed_items: items, last_updated: new Date().toISOString() })
            .eq('id', existing.id);
          if (updErr) throw updErr;
        } else {
          const { error: insErr } = await supabase
            .from('checklist_progress')
            .insert({
              user_id: notification.trainee_staff_id,
              procedure_id: proc.id,
              organization_id: notification.organization_id,
              role: notification.role,
              completed_items: items,
            });
          if (insErr) throw insErr;
        }
      }

      // 3. 通知完了
      const { error: nErr } = await supabase
        .from('instruction_notifications')
        .update({ status: 'completed', completed_at: new Date().toISOString() })
        .eq('id', notification.id);
      if (nErr) throw nErr;

      await writeClientAudit({
        organizationId: notification.organization_id,
        actionType: 'update',
        targetTable: 'instruction_notifications',
        targetId: notification.id,
        targetSummary: `指導通知を完了処理 (被指導=${notification.trainee_staff_id}, role=${notification.role}, ${procedures.length} 術式分の経験録・進捗を更新)`,
        changes: { trainee_staff_id: notification.trainee_staff_id, role: notification.role, procedureCount: procedures.length },
        success: true,
      });

      onClose();
    } catch (e) {
      console.error('保存エラー:', e);
      await writeClientAudit({
        organizationId: notification.organization_id,
        actionType: 'update',
        targetTable: 'instruction_notifications',
        targetId: notification.id,
        targetSummary: `指導通知の完了処理に失敗`,
        success: false,
        errorMessage: e.message,
      });
      setError(e.message || '保存に失敗しました');
    } finally {
      setSaving(false);
    }
  };

  const handleDismiss = async () => {
    if (!window.confirm('この通知を却下しますか？\n（経験録は更新されません。後で経験録ページから直接編集できます）')) return;
    try {
      await supabase
        .from('instruction_notifications')
        .update({ status: 'dismissed', dismissed_at: new Date().toISOString() })
        .eq('id', notification.id);
      onClose();
    } catch (e) {
      setError(e.message || '却下に失敗しました');
    }
  };

  const handleGoToSkillChart = () => {
    navigate(`/skills/chart/${notification.trainee_staff_id}`);
    onClose();
  };

  const roleLabel = notification.role === 'scrub' ? '器械出し' : '外回り';

  const calcChecklistStats = (procId) => {
    const checklist = checklistsByProcId[procId] || [];
    const progress = progressByProcId[procId] || [];
    const total = checklist.reduce((sum, cat) => sum + (cat.items?.length || 0), 0);
    return { total, done: progress.length };
  };

  return createPortal(
    <>
    <SavingOverlay visible={saving} message="経験録を保存中..." />
    <div style={styles.backdrop} onClick={saving ? undefined : onClose}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div style={styles.header}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <h2 style={styles.title}>経験録の更新</h2>
            <div style={styles.headerMeta}>
              <span style={styles.headerChip}>{notification.surgery_date}</span>
              <span style={styles.headerChip}>{roleLabel}</span>
              <span style={styles.headerSurgery}>{notification.surgery_procedure_name || '手術'}</span>
            </div>
            <div style={styles.traineeRow}>
              <span style={styles.traineeLabelText}>育成対象</span>
              <strong style={styles.traineeName}>
                {traineeName || `(社員番号 ${notification.trainee_employee_id || '不明'})`}
              </strong>
            </div>
          </div>
          <button onClick={onClose} style={styles.closeBtn} title="閉じる"><X size={20} /></button>
        </div>

        {/* Progress summary */}
        {!loading && procedures.length > 0 && (
          <div style={styles.summaryBar}>
            <span style={canSave ? styles.summaryReady : styles.summaryUnready}>
              {canSave
                ? '✓ 記入完了。保存できます'
                : `あと ${unreadyCount} 件の術式に「記入済み」チェックが必要です`}
            </span>
          </div>
        )}

        {/* Body */}
        <div style={styles.body}>
          {loading ? (
            <div style={styles.loading}>読み込み中...</div>
          ) : procedures.length === 0 ? (
            <div style={styles.empty}>
              <AlertCircle size={32} color="#f59e0b" />
              <p>この手術には経験録対象の術式が設定されていません。</p>
              <p style={{ fontSize: '0.85rem', color: '#6b7280' }}>
                手術の「必要術式・スキル設定」を確認してください。
              </p>
            </div>
          ) : (
            procedures.map((proc, idx) => {
              const checklist = checklistsByProcId[proc.id] || [];
              const progress = progressByProcId[proc.id] || [];
              const stats = calcChecklistStats(proc.id);
              const hasChecklist = checklist.length > 0;
              const isProcReady = procReady(proc.id);

              return (
                <div
                  key={proc.id}
                  style={{
                    ...styles.section,
                    borderColor: isProcReady ? '#86efac' : '#e5e7eb',
                    background: isProcReady ? '#f0fdf4' : '#fafafa',
                  }}
                >
                  <div style={styles.sectionHeader}>
                    <span style={styles.sectionIndex}>{idx + 1}</span>
                    <h3 style={styles.sectionTitle}>{proc.name || proc.id}</h3>
                    {isProcReady && (
                      <CheckCircle2 size={18} color="#16a34a" style={{ marginLeft: 'auto' }} />
                    )}
                  </div>

                  {/* スキルレベル */}
                  <div style={styles.subSection}>
                    <div style={styles.subSectionHeader}>
                      <BookOpen size={14} color="#6366f1" />
                      <span style={styles.subSectionTitle}>スキルレベル</span>
                      <label style={{
                        ...styles.markCheck,
                        ...(skillMarked[proc.id] ? styles.markCheckOn : {}),
                      }}>
                        <input
                          type="checkbox"
                          checked={!!skillMarked[proc.id]}
                          onChange={(e) => setSkillMarked(prev => ({ ...prev, [proc.id]: e.target.checked }))}
                          style={styles.markCheckInput}
                        />
                        記入済み
                      </label>
                    </div>
                    <div style={styles.skillBody}>
                      <div style={styles.skillCurrent}>
                        現在：<strong>{existingByProcedureId[proc.id]?.level || <span style={{ color: '#9ca3af' }}>未登録</span>}</strong>
                      </div>
                      <select
                        value={levels[proc.id] || ''}
                        onChange={(e) => setLevels(prev => ({ ...prev, [proc.id]: e.target.value }))}
                        style={styles.select}
                      >
                        <option value="">— 更新後を選択 —</option>
                        {SKILL_LEVELS.map((lv) => (
                          <option key={lv} value={lv}>{lv}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {/* チェックリスト */}
                  {hasChecklist ? (
                    <div style={styles.subSection}>
                      <div style={styles.subSectionHeader}>
                        <ListChecks size={14} color="#6366f1" />
                        <span style={styles.subSectionTitle}>
                          チェックリスト <span style={styles.subSectionMeta}>{stats.done}/{stats.total} 完了</span>
                        </span>
                        <label style={{
                          ...styles.markCheck,
                          ...(checklistMarked[proc.id] ? styles.markCheckOn : {}),
                        }}>
                          <input
                            type="checkbox"
                            checked={!!checklistMarked[proc.id]}
                            onChange={(e) => setChecklistMarked(prev => ({ ...prev, [proc.id]: e.target.checked }))}
                            style={styles.markCheckInput}
                          />
                          記入済み
                        </label>
                      </div>
                      <div style={styles.checklistBody}>
                        {checklist.map((category, catIdx) => (
                          <div key={catIdx} style={styles.category}>
                            <div style={styles.categoryHeader}>【{category.category || category.name || ''}】</div>
                            <div style={styles.categoryItems}>
                              {(category.items || []).map((item) => {
                                const completed = progress.find((p) => p.item_id === item.id);
                                return (
                                  <label key={item.id} style={styles.itemRow}>
                                    <input
                                      type="checkbox"
                                      checked={!!completed}
                                      onChange={() => toggleChecklistItem(proc.id, item.id)}
                                      style={styles.checkbox}
                                    />
                                    <span style={{
                                      ...styles.itemLabel,
                                      ...(completed ? styles.itemLabelDone : {}),
                                    }}>
                                      {item.text || item.label || ''}
                                    </span>
                                    {completed && (
                                      <span style={styles.itemMeta}>
                                        ✓ {completed.completed_at} ／ {completed.completed_by}
                                      </span>
                                    )}
                                  </label>
                                );
                              })}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <div style={styles.noChecklist}>※ この術式にはチェックリストが設定されていません</div>
                  )}
                </div>
              );
            })
          )}

          {error && <div style={styles.error}>{error}</div>}
        </div>

        {/* Footer */}
        <div style={styles.footer}>
          <button onClick={handleDismiss} style={styles.dismissBtn}>却下</button>
          <button onClick={handleGoToSkillChart} style={styles.detailBtn}>
            <ExternalLink size={14} style={{ display: 'inline', marginRight: 4 }} />
            詳細編集
          </button>
          <button
            onClick={handleSave}
            disabled={saving || loading || !canSave}
            style={{
              ...styles.saveBtn,
              ...(saving || loading || !canSave ? styles.saveBtnDisabled : {}),
            }}
          >
            <CheckCircle2 size={16} style={{ display: 'inline', marginRight: 6, verticalAlign: '-3px' }} />
            {saving ? '保存中...' : canSave ? '保存して完了' : `未記入 ${unreadyCount} 件`}
          </button>
        </div>
      </div>
    </div>
    </>,
    document.body
  );
}

const styles = {
  backdrop: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(15, 23, 42, 0.65)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 10002,
    padding: '1rem',
  },
  modal: {
    background: '#fff',
    borderRadius: 14,
    width: '100%',
    maxWidth: 760,
    maxHeight: '92vh',
    display: 'flex',
    flexDirection: 'column',
    boxShadow: '0 24px 64px rgba(0,0,0,0.35)',
    fontFamily: 'system-ui, -apple-system, sans-serif',
  },

  // Header
  header: {
    padding: '1.25rem 1.5rem',
    borderBottom: '1px solid #e5e7eb',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: '1rem',
    background: 'linear-gradient(180deg, #eef2ff 0%, #ffffff 100%)',
    borderRadius: '14px 14px 0 0',
  },
  title: { fontSize: '1.15rem', fontWeight: 700, color: '#111827', margin: '0 0 8px 0' },
  headerMeta: { display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 6 },
  headerChip: {
    fontSize: '0.75rem',
    background: '#eef2ff',
    color: '#4338ca',
    padding: '2px 10px',
    borderRadius: 999,
    fontWeight: 600,
  },
  headerSurgery: { fontSize: '0.85rem', color: '#374151', fontWeight: 500 },
  traineeRow: { display: 'flex', alignItems: 'center', gap: 8 },
  traineeLabelText: { fontSize: '0.75rem', color: '#6b7280', fontWeight: 600 },
  traineeName: { fontSize: '0.95rem', color: '#1f2937' },
  closeBtn: {
    background: 'transparent',
    border: 'none',
    color: '#6b7280',
    cursor: 'pointer',
    padding: 4,
    flexShrink: 0,
  },

  // Summary bar
  summaryBar: {
    padding: '0.5rem 1.5rem',
    fontSize: '0.85rem',
    fontWeight: 600,
    borderBottom: '1px solid #e5e7eb',
  },
  summaryReady: { color: '#16a34a' },
  summaryUnready: { color: '#b45309' },

  // Body
  body: { padding: '1rem 1.5rem', overflowY: 'auto', flex: 1, background: '#f9fafb' },

  loading: { padding: '3rem', textAlign: 'center', color: '#9ca3af' },
  empty: {
    padding: '2rem',
    textAlign: 'center',
    color: '#6b7280',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '0.75rem',
  },

  // Section（術式 1 つの単位）
  section: {
    border: '1.5px solid #e5e7eb',
    borderRadius: 10,
    padding: '0.85rem 1rem 1rem 1rem',
    marginBottom: '1rem',
    transition: 'all 0.2s ease',
  },
  sectionHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    marginBottom: '0.75rem',
  },
  sectionIndex: {
    background: '#6366f1',
    color: '#fff',
    width: 24,
    height: 24,
    borderRadius: 999,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '0.8rem',
    fontWeight: 700,
    flexShrink: 0,
  },
  sectionTitle: { fontSize: '1rem', fontWeight: 700, color: '#1f2937', margin: 0 },

  // Sub-section（スキル or チェックリスト）
  subSection: {
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: 8,
    marginTop: 8,
    overflow: 'hidden',
  },
  subSectionHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '0.55rem 0.85rem',
    background: '#f9fafb',
    borderBottom: '1px solid #e5e7eb',
  },
  subSectionTitle: { fontSize: '0.85rem', fontWeight: 600, color: '#1f2937', flex: 1 },
  subSectionMeta: { fontSize: '0.75rem', color: '#6366f1', fontWeight: 600, marginLeft: 6 },

  // 記入済み チェック
  markCheck: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    padding: '3px 10px',
    background: '#f3f4f6',
    color: '#6b7280',
    fontSize: '0.75rem',
    fontWeight: 600,
    borderRadius: 999,
    cursor: 'pointer',
    border: '1px solid #e5e7eb',
    flexShrink: 0,
  },
  markCheckOn: {
    background: '#dcfce7',
    color: '#166534',
    borderColor: '#86efac',
  },
  markCheckInput: { accentColor: '#16a34a', width: 14, height: 14 },

  // スキル本体
  skillBody: {
    padding: '0.75rem 0.85rem',
    display: 'flex',
    alignItems: 'center',
    gap: '0.85rem',
    flexWrap: 'wrap',
  },
  skillCurrent: { fontSize: '0.85rem', color: '#4b5563' },
  select: {
    padding: '0.45rem 0.85rem',
    fontSize: '0.9rem',
    border: '1.5px solid #d1d5db',
    borderRadius: 6,
    outline: 'none',
    background: '#fff',
    minWidth: 180,
  },

  // チェックリスト本体
  checklistBody: { padding: '0.5rem 0.85rem 0.85rem 0.85rem' },
  category: { padding: '0.5rem 0 0.25rem 0' },
  categoryHeader: { fontSize: '0.82rem', fontWeight: 700, color: '#374151', marginBottom: '0.35rem' },
  categoryItems: { display: 'flex', flexDirection: 'column', gap: '0.2rem' },
  itemRow: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 8,
    padding: '0.3rem 0.5rem',
    cursor: 'pointer',
    fontSize: '0.85rem',
    borderRadius: 4,
  },
  checkbox: { marginTop: 3, accentColor: '#6366f1', flexShrink: 0 },
  itemLabel: { flex: 1, color: '#1f2937', lineHeight: 1.45 },
  itemLabelDone: { color: '#6b7280', textDecoration: 'line-through' },
  itemMeta: { fontSize: '0.7rem', color: '#16a34a', whiteSpace: 'nowrap' },
  noChecklist: {
    fontSize: '0.8rem',
    color: '#9ca3af',
    background: '#fff',
    border: '1px dashed #e5e7eb',
    borderRadius: 6,
    padding: '0.6rem 0.85rem',
    marginTop: 8,
    textAlign: 'center',
  },

  // Error
  error: {
    background: '#fef2f2',
    border: '1px solid #fca5a5',
    color: '#b91c1c',
    padding: '0.75rem 1rem',
    borderRadius: 8,
    fontSize: '0.85rem',
    marginTop: '0.75rem',
  },

  // Footer
  footer: {
    padding: '0.85rem 1.5rem',
    borderTop: '1px solid #e5e7eb',
    background: '#fff',
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '0.5rem',
    flexWrap: 'wrap',
    borderRadius: '0 0 14px 14px',
  },
  dismissBtn: {
    padding: '0.55rem 1rem',
    background: '#fff',
    color: '#991b1b',
    border: '1px solid #fca5a5',
    borderRadius: 6,
    cursor: 'pointer',
    fontSize: '0.85rem',
    fontWeight: 500,
  },
  detailBtn: {
    padding: '0.55rem 1rem',
    background: '#fff',
    color: '#374151',
    border: '1px solid #d1d5db',
    borderRadius: 6,
    cursor: 'pointer',
    fontSize: '0.85rem',
    fontWeight: 500,
  },
  saveBtn: {
    padding: '0.55rem 1.5rem',
    background: '#16a34a',
    color: '#fff',
    border: 'none',
    borderRadius: 6,
    cursor: 'pointer',
    fontSize: '0.95rem',
    fontWeight: 700,
    boxShadow: '0 2px 6px rgba(22,163,74,0.25)',
  },
  saveBtnDisabled: {
    background: '#9ca3af',
    cursor: 'not-allowed',
    boxShadow: 'none',
  },
};
